import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, ArrowUpRight, Globe2, ShieldCheck } from "lucide-react";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden border-t border-white/10 bg-[#080510]">
      <div className="pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full bg-pink-600/10 blur-[130px]" />

      <div className="pointer-events-none absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-violet-700/10 blur-[130px]" />

      <div className="relative mx-auto w-full px-4 py-14 sm:px-6 lg:w-[80%]">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <Link to="/" className="group inline-flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 rounded-2xl bg-pink-500/20 blur-xl transition group-hover:bg-pink-500/40" />

                <div className="relative grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-white/[0.06]">
                  <img
                    src="/logo.png"
                    width={48}
                    height={46}
                    alt="OjMee Technology Logo"
                    className="object-contain"
                  />
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-black tracking-tight text-white">
                  OjMee Technology
                </h2>

                <p className="mt-0.5 text-[14px] text-pink-300">
                  Innovation • Technology • Future
                </p>
              </div>
            </Link>

            <p className="mt-6 max-w-xl text-sm leading-7 text-slate-400">
              We build AI-powered websites, mobile applications and intelligent
              business software for modern organizations. From ERP, HRMS, CRM,
              billing and dashboards to automation, cloud solutions and custom
              digital platforms — we turn complex business requirements into
              scalable technology.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-semibold text-slate-300">
                <Globe2 className="h-4 w-4 text-pink-300" />
                Digital Solutions
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-semibold text-slate-300">
                <ShieldCheck className="h-4 w-4 text-violet-300" />
                Secure & Scalable
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-5 text-sm font-black uppercase tracking-widest text-white">
              Company
            </h3>

            <div className="grid gap-3">
              {[
                ["About Us", "/about"],
                ["Services", "/services"],
                ["Products", "/products"],
                ["Portfolio", "/portfolio"],
                ["Careers", "/careers"],
                ["Contact Us", "/contact"],
              ].map(([label, path]) => (
                <Link
                  key={path}
                  to={path}
                  className="group flex w-fit items-center gap-2 text-sm text-slate-400 transition-colors duration-200 hover:text-pink-300"
                >
                  <span className="h-px w-0 bg-pink-400 transition-all duration-300 group-hover:w-4" />

                  {label}

                  <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:opacity-100" />
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-5 text-sm font-black uppercase tracking-widest text-white">
              Contact Us
            </h3>

            <div className="space-y-3">
              <a
                href="mailto:ojmeetech@gmail.com"
                className="group -mx-3 flex items-start gap-3 rounded-2xl border border-transparent p-3 transition-all duration-300 hover:border-white/10 hover:bg-white/[0.04]"
              >
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-violet-500/10">
                  <Mail className="h-4 w-4 text-violet-300" />
                </div>

                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
                    Email Us
                  </p>

                  <p className="mt-1 break-all text-sm font-semibold text-slate-300 transition group-hover:text-violet-300">
                    ojmeetech@gmail.com
                  </p>
                </div>
              </a>

              <div className="-mx-3 flex items-start gap-3 rounded-2xl border border-transparent p-3">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-pink-500/10">
                  <MapPin className="h-4 w-4 text-pink-300" />
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
                      Head Office
                    </p>

                    <span className="rounded-full border border-pink-400/15 bg-pink-400/[0.07] px-2 py-0.5 text-[8px] font-bold uppercase tracking-wider text-pink-300">
                      Delhi
                    </span>
                  </div>

                  <p className="mt-1.5 text-sm leading-6 text-slate-400">
                    C-58, Shahpuri Tower,
                    <br />
                    Janakpuri, Delhi - 110058
                  </p>
                </div>
              </div>

              <Link
                to="/contact"
                className="group mt-2 inline-flex items-center gap-2 text-xs font-bold text-pink-300 transition hover:text-pink-200"
              >
                View all office locations
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="mx-auto w-full px-4 py-5 sm:px-6 lg:w-[80%]">
          <div className="flex flex-col gap-4 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
            <p className="text-xs text-slate-500">
              © 2022 - {currentYear}{" "}
              <span className="font-semibold text-slate-400">
                OjMee Technology
              </span>
              . All Rights Reserved.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-600 sm:justify-end">
              <Link to="/privacy" className="transition hover:text-pink-300">
                Privacy Policy
              </Link>

              <span className="h-3 w-px bg-white/10" />

              <Link to="/terms" className="transition hover:text-pink-300">
                Terms & Conditions
              </Link>

              <span className="h-3 w-px bg-white/10" />

              <Link to="/contact" className="transition hover:text-pink-300">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
